import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, viewChild, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatListItemIcon, MatListItemTitle, MatListOption, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { FilterSavedState } from '@src/suite/common/store/inline-filter/inline-filter.models';
import { applyFilter, buildReverseIndex, mergeFilters } from '@src/suite/common/utils/section-filter-utils';

import { ClickStopPropagationDirective, EnterStopPropagationDirective, SpaceStopPropagationDirective } from '../../directives/stop-propagation';
import { FilterTriggerComponent } from '../filter/filter-trigger/filter-trigger';
import { StoryIconComponent } from '../icon/story-icon';

// Filter popup hangs off the LEFT of the menu (right edge of overlay = left edge of menu origin).
// Falls back to the right if no room on the left.
const FILTER_OVERLAY_POSITIONS: ConnectedPosition[] = [
    { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
];

@Component({
    selector: 'st-story-template-menu',
    imports: [
        CdkConnectedOverlay,
        CdkOverlayOrigin,
        ClickStopPropagationDirective,
        EnterStopPropagationDirective,
        FilterTriggerComponent,
        MatFormField,
        MatIcon,
        MatIconButton,
        MatInput,
        MatListItemIcon,
        MatListItemTitle,
        MatListOption,
        MatPrefix,
        MatSelectionList,
        MatSuffix,
        MatTooltip,
        SpaceStopPropagationDirective,
        StoryIconComponent,
    ],
    templateUrl: './story-template-menu.html',
    styleUrl: './story-template-menu.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-template-menu',
    },
})
export class StoryTemplateMenuComponent {
    private store = inject(Store);
    readonly i18n = this.store.selectSignal(selectI18N);

    readonly templates = input<StorytellingStoryRecord[]>([]);
    readonly disabled = input<boolean>(false);
    readonly dir = input<'ltr' | 'rtl'>('ltr');
    readonly cardDisabled = input<boolean>(false);
    readonly tooltip = input<string>('');
    readonly filterConfig = input<StorytellingFilterDefinition[]>([]);
    readonly filterState = input<FilterSavedState>({});

    readonly templatesSelected = output<StorytellingStoryRecord[]>();
    readonly filterStateChange = output<FilterSavedState>();
    readonly filterSave = output<void>();
    readonly filterRevert = output<void>();
    readonly filterClear = output<void>();

    readonly open = signal(false);
    private readonly selected = signal<StorytellingStoryRecord[]>([]);
    readonly searchQuery = signal('');

    readonly filterOverlayPositions = FILTER_OVERLAY_POSITIONS;

    private readonly selectionList = viewChild<MatSelectionList>('selectionList');
    private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

    private readonly index = computed(() => buildReverseIndex(this.templates() ?? [], this.filterConfig()));
    private readonly merged = computed(() => mergeFilters(this.filterConfig(), this.filterState()));

    readonly filteredTemplates = computed(() => {
        const filtered = applyFilter(this.templates() ?? [], this.merged(), this.index());
        const query = this.searchQuery().trim().toLowerCase();
        if (!query) return filtered;
        return filtered.filter(t => (t?.Title ?? '').toLowerCase().includes(query));
    });

    toggle() {
        if (this.open()) {
            this.close();
        } else {
            this.openMenu();
        }
    }

    openMenu() {
        this.searchQuery.set('');
        this.selected.set([]);
        this.open.set(true);
    }

    onOverlayAttached() {
        this.searchInput()?.nativeElement.focus();
    }

    close() {
        if (!this.open()) return;
        this.open.set(false);
        const picked = this.selected();
        this.selected.set([]);
        this.searchQuery.set('');
        if (picked.length) {
            this.templatesSelected.emit(picked);
        }
    }

    onSelectionListChange(_change: MatSelectionListChange) {
        const list = this.selectionList();
        if (!list) return;
        this.selected.set(list.selectedOptions.selected.map(o => o.value as StorytellingStoryRecord));
    }

    onTriggerArrowDown(event: Event) {
        event.preventDefault();
        if (!this.open()) {
            this.open.set(true);
            queueMicrotask(() => this.selectionList()?.options.first?.focus());
        } else {
            this.selectionList()?.options.first?.focus();
        }
    }

    onTriggerArrowUp(event: Event) {
        event.preventDefault();
        if (!this.open()) {
            this.open.set(true);
            queueMicrotask(() => this.selectionList()?.options.last?.focus());
        } else {
            this.selectionList()?.options.last?.focus();
        }
    }

    onListKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' || (event.key === 'Tab' && !event.shiftKey)) {
            event.preventDefault();
            event.stopPropagation();
            this.close();
        }
    }

    onSearchInput(query: string) {
        this.searchQuery.set(query);
    }
}
